import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { cn } from '@/lib/utils'
import type { BlackjackPlayingCard, BlackjackRoundStatus, BlackjackSplitPhase, BlackjackSuit } from '@/types/blackjack'
import { AssetManager } from '../core/AssetManager'
import { ResultModal } from './ResultModal'

const DEAL_STAGGER_MS = 140
const DEAL_DURATION_MS = 480

const suitAssetKey = {
	Hearts: 'suitHearts',
	Diamonds: 'suitDiamonds',
	Clubs: 'suitClubs',
	Spades: 'suitSpades',
} as const

function isRedSuit(suit: BlackjackSuit) {
	return suit === 'Hearts' || suit === 'Diamonds'
}

function SuitImage({ suit, className }: { suit: BlackjackSuit; className?: string }) {
	return <img src={AssetManager.getImageUrl(suitAssetKey[suit])} alt='' draggable={false} className={cn('pointer-events-none select-none object-contain', className)} />
}

function CardFace({ card }: { card: BlackjackPlayingCard }) {
	const red = isRedSuit(card.suit)
	const ink = red
		? 'linear-gradient(180deg, hsl(var(--destructive)) 0%, hsl(var(--destructive)/0.8) 55%, hsl(var(--destructive)/0.5) 100%)'
		: 'linear-gradient(180deg, hsl(var(--card)) 0%, hsl(var(--background)) 50%, hsl(var(--secondary)) 100%)'

	return (
		<div
			className='relative flex h-full w-full flex-col overflow-hidden rounded-[inherit]'
			style={{
				background: 'linear-gradient(180deg, hsl(var(--foreground)) 0%, hsl(var(--foreground)/0.98) 48%, hsl(var(--foreground)/0.94) 100%)',
				boxShadow: 'inset 0 1px 0 hsl(var(--foreground)/0.85), inset 0 -1px 0 hsl(var(--background)/0.08)',
			}}
		>
			<div className='absolute left-[6%] top-[5%] z-10 flex flex-col items-center leading-none'>
				<span
					className='text-[1.25em] font-bold md:text-[1.75em]'
					style={{
						backgroundImage: ink,
						WebkitBackgroundClip: 'text',
						backgroundClip: 'text',
						color: 'transparent',
					}}
				>
					{card.rank}
				</span>
			</div>

			<div className='absolute inset-0 flex items-center justify-center'>
				<SuitImage suit={card.suit} className='h-[50%] w-[50%] drop-shadow-[0_1px_2px_hsl(var(--background)/0.18)]' />
			</div>
		</div>
	)
}

function PlayingCardView({
	card,
	className = '',
	style,
	entering = false,
	enterDelayMs = 0,
	isDealer = false,
	onEntered,
}: {
	card?: BlackjackPlayingCard
	className?: string
	style?: CSSProperties
	entering?: boolean
	enterDelayMs?: number
	isDealer?: boolean
	onEntered?: () => void
}) {
	const isHidden = !card || card.rank === ''
	const [placed, setPlaced] = useState(!entering)
	const enteredRef = useRef(false)
	const onEnteredRef = useRef(onEntered)
	onEnteredRef.current = onEntered

	useEffect(() => {
		if (!entering) {
			setPlaced(true)
			return
		}

		enteredRef.current = false
		setPlaced(false)

		const placeTimer = window.setTimeout(() => {
			requestAnimationFrame(() => {
				requestAnimationFrame(() => setPlaced(true))
			})
		}, enterDelayMs)
		const doneTimer = window.setTimeout(
			() => {
				if (enteredRef.current) {
					return
				}
				enteredRef.current = true
				onEnteredRef.current?.()
			},
			enterDelayMs + DEAL_DURATION_MS + 40,
		)

		return () => {
			window.clearTimeout(placeTimer)
			window.clearTimeout(doneTimer)
		}
	}, [entering, enterDelayMs])

	const dealFromStyle: CSSProperties = {
		right: isDealer ? '4%' : '6%',
		top: isDealer ? '-30%' : '-220%',
		transform: 'translate(30%, -40%) scale(0.88) rotate(-12deg)',
		opacity: 0,
		zIndex: 30,
		transitionDelay: '0ms',
	}

	const resolvedStyle: CSSProperties = {
		perspective: '1000px',
		aspectRatio: '3 / 4.6',
		transition: `top ${DEAL_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1), right ${DEAL_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1), transform ${DEAL_DURATION_MS}ms cubic-bezier(0.22, 1, 0.36, 1), opacity ${DEAL_DURATION_MS}ms ease`,
		...(placed ? style : dealFromStyle),
	}

	return (
		<div className='absolute mx-1 w-[7%] min-w-[40px] rounded-sm md:w-[9%] md:min-w-[50px]' style={resolvedStyle}>
			<div
				className='relative h-full w-full rounded-sm shadow-[0_2px_6px_hsl(var(--background)/0.45)]'
				style={{
					transformStyle: 'preserve-3d',
					transform: isHidden ? 'rotateY(180deg)' : 'rotateY(0deg)',
					transition: 'transform 0.45s ease-in-out',
					transitionDelay: placed && !isHidden ? '80ms' : '0ms',
				}}
			>
				<div
					className={cn('absolute inset-0 overflow-hidden rounded-sm outline outline-[4px] md:rounded-sm md:outline-[6px]', className)}
					style={{
						backfaceVisibility: 'hidden',
						WebkitBackfaceVisibility: 'hidden',
						transform: 'rotateY(0deg)',
					}}
				>
					{card && card.rank ? <CardFace card={card} /> : null}
				</div>

				<div
					className='absolute inset-0 overflow-hidden rounded-sm md:rounded-sm'
					style={{
						backfaceVisibility: 'hidden',
						WebkitBackfaceVisibility: 'hidden',
						transform: 'rotateY(180deg)',
					}}
				>
					<img src={AssetManager.getImageUrl('cardBack')} alt='' className='h-full w-full object-cover' draggable={false} />
				</div>
			</div>
		</div>
	)
}

function DeckStack() {
	return (
		<div className='absolute right-[2.7%] top-[7.4%] w-[7%] md:w-[9%]'>
			{Array.from({ length: 5 }, (_, key) => (
				<PlayingCardView
					key={key}
					style={{
						top: '10%',
						right: '3%',
						width: '100%',
						transform: `translate(0px,${-key * 2}px)`,
					}}
				/>
			))}
		</div>
	)
}

function HandPanel({
	hand,
	handValue,
	status,
	isClearing,
	isDealer = false,
	isMain = false,
	splitPhase = 'none',
}: {
	hand: BlackjackPlayingCard[]
	handValue: number
	status: BlackjackRoundStatus
	isClearing: boolean
	isDealer?: boolean
	isMain?: boolean
	splitPhase?: BlackjackSplitPhase
}) {
	const settledIndicesRef = useRef<Set<number>>(new Set())
	const enteringRef = useRef<Record<number, number>>({})
	const [, bumpEnterVersion] = useState(0)

	if (hand.length === 0) {
		if (settledIndicesRef.current.size > 0 || Object.keys(enteringRef.current).length > 0) {
			settledIndicesRef.current = new Set()
			enteringRef.current = {}
		}
	} else {
		for (const index of [...settledIndicesRef.current]) {
			if (index >= hand.length) {
				settledIndicesRef.current.delete(index)
			}
		}

		const freshIndices: number[] = []
		for (let index = 0; index < hand.length; index += 1) {
			if (!settledIndicesRef.current.has(index) && !(index in enteringRef.current)) {
				freshIndices.push(index)
			}
		}

		if (freshIndices.length > 0) {
			const nextEntering = { ...enteringRef.current }
			const baseOrder = Object.keys(nextEntering).length
			freshIndices.forEach((index, order) => {
				nextEntering[index] = (baseOrder + order) * DEAL_STAGGER_MS
			})
			enteringRef.current = nextEntering
		}
	}

	const enteringByIndex = enteringRef.current

	const markEntered = (index: number) => {
		if (settledIndicesRef.current.has(index) && !(index in enteringRef.current)) {
			return
		}

		settledIndicesRef.current.add(index)
		if (!(index in enteringRef.current)) {
			return
		}

		const { [index]: _removed, ...rest } = enteringRef.current
		enteringRef.current = rest
		bumpEnterVersion((version) => version + 1)
	}

	let outlineClass = 'outline-none'
	let badgeClass = cn(
		handValue ? 'flex' : 'hidden',
		'absolute items-center justify-center rounded-full px-1.5 text-[10px] font-semibold leading-none tracking-wide',
		'h-5 min-w-5 shadow-sm backdrop-blur-[2px] md:h-6 md:min-w-6 md:px-2 md:text-xs',
	)

	if (!isDealer) {
		outlineClass = ' -outline-offset-1 '
		const applyMatchStyle = !splitPhase || splitPhase === 'none' || isMain
		if (applyMatchStyle) {
			switch (status) {
				case 'win':
					outlineClass += ' outline-emerald-400'
					badgeClass = cn(badgeClass, 'bg-emerald-500/95 text-white')
					break
				case 'lost':
					outlineClass += ' outline-destructive'
					badgeClass = cn(badgeClass, 'bg-destructive/95 text-destructive-foreground')
					break
				case 'draw':
					outlineClass += ' outline-amber-500'
					badgeClass = cn(badgeClass, 'bg-amber-500/95 text-white')
					break
				default:
					outlineClass += ' outline-none'
					badgeClass = cn(badgeClass, 'bg-white/90 text-zinc-800')
			}
		} else {
			outlineClass += ' outline-none'
			badgeClass = cn(badgeClass, 'bg-white/90 text-zinc-800')
		}
	} else {
		badgeClass = cn(badgeClass, 'bg-white/90 text-zinc-800')
	}

	let right = 50
	if (splitPhase !== 'none') {
		if (isMain) {
			right = splitPhase === 'firstHandActive' ? 30 : 70
		} else {
			right = splitPhase === 'firstHandActive' ? 70 : 30
		}
	}

	const badgeReady = hand.length > 0 && Object.keys(enteringByIndex).length === 0

	return (
		<>
			{hand.map((card, index) => {
				const entering = index in enteringByIndex
				const style: CSSProperties = {
					right: `${right}%`,
					top: '50%',
					transform: `translate(${
						isClearing ? 30 - 60 * ((hand.length + 1) / 2 - (index + 1)) : 50 - 60 * ((hand.length + 1) / 2 - (index + 1))
					}%,${isClearing ? -20 - 8 * (hand.length - (index + 1)) : -40 - 8 * (hand.length - (index + 1))}%)`,
					opacity: isClearing ? 0 : 1,
					transitionDelay: isClearing ? `${index * 0.2}s` : '0s',
					zIndex: entering ? 30 : index + 1,
				}

				return (
					<PlayingCardView
						key={`hand-card-${index}`}
						card={card}
						className={outlineClass}
						style={style}
						entering={entering}
						enterDelayMs={enteringByIndex[index] ?? 0}
						isDealer={isDealer}
						onEntered={() => markEntered(index)}
					/>
				)
			})}

			<div
				className={badgeClass}
				style={{
					right: `${right}%`,
					top: '-70%',
					transition: 'all 0.45s ease',
					transform: `translate(${25 * hand.length + 60}%,0%)`,
					opacity: isClearing || !badgeReady ? 0 : 1,
					transitionDelay: isClearing ? `${(hand.length - 1) * 0.2}s` : '0.15s',
				}}
			>
				{handValue}
			</div>
		</>
	)
}

interface BlackjackTableProps {
	dealerHand: BlackjackPlayingCard[]
	playerHand: BlackjackPlayingCard[]
	playerHandTwo: BlackjackPlayingCard[]
	dealerValue: number
	playerValue: number
	playerValue2: number
	splitPhase: BlackjackSplitPhase
	status: BlackjackRoundStatus
	isClearing: boolean
	isResultVisible: boolean
	multiplier: number
	profit: number
	currencyCode: string
	onDismissResult: () => void
}

export function BlackjackTable({
	dealerHand,
	playerHand,
	playerHandTwo,
	dealerValue,
	playerValue,
	playerValue2,
	splitPhase,
	status,
	isClearing,
	isResultVisible,
	multiplier,
	profit,
	currencyCode,
	onDismissResult,
}: BlackjackTableProps) {
	const hasSplit = splitPhase !== 'none'

	return (
		<div
			className='relative h-full min-h-0 w-full overflow-hidden rounded-xl bg-cover bg-center bg-no-repeat'
			style={{ backgroundImage: `url(${AssetManager.getImageUrl('tableFelt')})` }}
		>
			<ResultModal visible={isResultVisible} multiplier={multiplier} profit={profit} currencyCode={currencyCode} onDismiss={onDismissResult} />

			<div className='flex h-full w-full space-x-1'>
				<DeckStack />

				<div className='grid w-full grid-rows-6 gap-1'>
					<div className='row-span-1 w-full' />
					<div className='relative row-span-1 w-full'>
						<HandPanel hand={dealerHand} handValue={dealerValue} status={status} isClearing={isClearing} isDealer />
					</div>
					<div className='row-span-2 w-full' />
					<div className='relative row-span-1 w-full'>
						{!hasSplit ? (
							<HandPanel hand={playerHand} handValue={playerValue} status={status} isClearing={isClearing} />
						) : (
							<div className='grid h-full w-full grid-cols-2'>
								<HandPanel
									hand={splitPhase === 'firstHandActive' ? playerHandTwo : playerHand}
									handValue={splitPhase === 'firstHandActive' ? playerValue2 : playerValue}
									status={status}
									isClearing={isClearing}
									isMain={splitPhase === 'secondHandActive'}
									splitPhase={splitPhase}
								/>
								<HandPanel
									hand={splitPhase === 'secondHandActive' ? playerHandTwo : playerHand}
									handValue={splitPhase === 'secondHandActive' ? playerValue2 : playerValue}
									status={status}
									isClearing={isClearing}
									isMain={splitPhase === 'firstHandActive'}
									splitPhase={splitPhase}
								/>
							</div>
						)}
					</div>
					<div className='row-span-1 w-full' />
				</div>
			</div>
		</div>
	)
}
